import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useApi } from '../hooks/useApi';

const PlanFeaturesContext = createContext(null);

// В проекте инструмент доступен, если он есть в тарифе хотя бы одного кабинета —
// бэкенд проверяет каждый кабинет отдельно.
function mergeFeatures(list) {
    const merged = {};
    list.forEach(features => {
        Object.entries(features || {}).forEach(([key, val]) => {
            const prev = merged[key];
            if (prev === undefined) merged[key] = val;
            else if (typeof val === 'number' && typeof prev === 'number') {
                merged[key] = prev < 0 || val < 0 ? -1 : Math.max(prev, val);
            } else if (!prev && val) merged[key] = val;
        });
    });
    return merged;
}

export function PlanFeaturesProvider({ accountId, accountIds, children }) {
    const { request } = useApi();
    const [features, setFeatures] = useState(null);
    const [planType, setPlanType] = useState(null);
    const [loading, setLoading] = useState(false);
    const lastKey = useRef(null);
    const ids = accountId ? [accountId] : (accountIds || []).filter(Boolean);
    const idsKey = ids.join(',');

    const load = useCallback(async (key) => {
        const list = key ? key.split(',') : [];
        if (!list.length) { setFeatures(null); setPlanType(null); return; }
        setLoading(true);
        try {
            const results = await Promise.allSettled(
                list.map(id => request(`/api/v1/billing/shops/${id}/plan`))
            );
            const plans = results.filter(r => r.status === 'fulfilled').map(r => r.value || {});
            if (!plans.length) throw new Error('no plans');
            setFeatures(mergeFeatures(plans.map(plan => plan.features || {})));
            setPlanType(plans[0].plan_type || null);
        } catch {
            setFeatures(null);
            setPlanType(null);
        } finally {
            setLoading(false);
        }
    }, [request]);

    useEffect(() => {
        if (idsKey === lastKey.current) return;
        lastKey.current = idsKey;
        load(idsKey);
    }, [idsKey, load]);

    const hasFeature = useCallback((key) => {
        if (features === null) return true; // ещё грузим — показываем всё
        const val = features[key];
        if (val === undefined) return true; // неизвестная фича — не блокируем
        if (typeof val === 'boolean') return val;
        if (typeof val === 'number') return val !== 0;
        return Boolean(val);
    }, [features]);

    return (
        <PlanFeaturesContext.Provider value={{ features, planType, hasFeature, reload: () => load(idsKey) }}>
            {children}
        </PlanFeaturesContext.Provider>
    );
}

export function usePlanFeatures() {
    const ctx = useContext(PlanFeaturesContext);
    if (!ctx) return { features: {}, planType: null, hasFeature: () => true, reload: () => {} };
    return ctx;
}
